// Ported from the native app's JournalPageView.swift — New Entry / Review
// tabs, position size calculator, tag chips, R stepper, notes overlay,
// image attach (including chart-window captures), stats, CSV export.

import { el, spacer, divider, svgIcon, dockIntoSidePanel, closeButton } from "../lib/dom.js";
import { storageGet, storageSet, onStorageChange } from "../lib/storage.js";
import { journalStore, type TradeDirection, type TradeEntry } from "../lib/journal-store.js";
import { allTimeStats, MIN_TRADES_FOR_STATS } from "../lib/journal-stats.js";
import { listSavedStrategies, loadStrategy } from "../lib/composable/storage.js";

const icons = {
  close: "M4 4l8 8M12 4l-8 8",
  closeCircle: "M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14zM5.5 5.5l5 5m0-5l-5 5",
  plus: "M8 3v10M3 8h10",
  photo: "M2 3h12v10H2zM5 8.5L7 11l2.5-3.5L14 13H2z",
  expand: "M3 13l4-4M3 13v-3.5M3 13h3.5M13 3L9 7M13 3v3.5M13 3H9.5",
  check: "M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14zM5 8l2 2 4-4",
  minusCircle: "M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14zM5 8h6",
  warning: "M8 2l6.5 11.5h-13zM8 7v3M8 12v.2",
  chevronDown: "M4 6l4 4 4-4",
  chevronUp: "M4 10l4-4 4 4",
  chart: "M2 13V3M2 13h11M5 11V8M8 11V5M11 11V9",
};

type JournalTab = "newEntry" | "review";

// Form state lives at module scope so flipping between popup pages doesn't
// wipe a half-written entry while the popup stays open.
const form = {
  // Review is the front door now — trades are logged from the chart (place a
  // position → Log trade), so the list of logged trades is what you land on.
  tab: "review" as JournalTab,
  activeTrader: "Me", // whose trades you're viewing/logging (Me / others / …)
  symbol: "",
  direction: "long" as TradeDirection,
  rMultiple: 0,
  entryPrice: "",
  exitPrice: "",
  selectedTag: "Breakout",
  // Strategy dev tie-in: link a trade to a saved strategy, and mark whether it
  // was manual / paper / live (practice vs real money) without extra fields.
  strategy: "",
  mode: "manual" as "manual" | "paper" | "live",
  // Extended (Sean-parity) fields — surfaced under a collapsible "Details".
  showDetails: false,
  session: "",
  dailyBias: "",
  targetPrice: "",
  stopPrice: "",
  entryType: "",
  size: "",
  confluence1: "",
  confluence2: "",
  ticks: "",
  maxTicks: "",
  riskPct: "",
  timeHeldMinutes: "",
  fees: "",
  netPnl: "",
  account: "",
  tvLink: "",
  thesis: "",
  whatHappened: "",
  lesson: "",
  attachedImage: null as string | null, // PNG data URL
  showAddTag: false,
  showSizeCalc: false,
  showNotes: false,
  reviewFilterSymbol: "",
  reviewFilterTag: "",
  reviewFilterStrategy: "",
  reviewFilterOutcome: "" as "" | "win" | "loss",
  reviewingEntryID: null as string | null,
  stopDistance: 0,
  /** Timeframe carried in from a chart capture, stored on the entry. */
  captureTimeframe: null as string | null,
};

/** Watchlist journal-chips land here: open Review pre-filtered to a symbol. */
export function presetJournalReviewFilter(symbol: string): void {
  form.tab = "review";
  form.reviewFilterSymbol = symbol;
  form.reviewFilterTag = "";
  form.reviewFilterOutcome = "";
}

let storeInitialized = false;
// The pending capture last shown in the form. The capture stays in storage
// until the trade is logged or the image is removed — clearing it on adopt
// lost the image whenever the adopting popup was immediately force-closed
// (e.g. the snip window's own close stealing focus milliseconds later).
let lastAdoptedCapture: string | null = null;

function fmtR(value: number): string {
  return `${value >= 0 ? "+" : ""}${value.toFixed(1)}R`;
}

/** Parse an optional numeric form field: blank/invalid → undefined. */
function numOrUndef(s: string): number | undefined {
  const t = s.trim();
  if (t === "") return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

/** The journal's view is scoped to the active trader — so "Me" analytics never
 * mix in an imported trader's trades, and switching traders
 * shows their book. */
function viewEntries(): TradeEntry[] {
  return journalStore.entries.filter((e) => (e.trader ?? "Me") === form.activeTrader);
}

/** All trader names, "Me" first, then persisted/added ones. */
function allTraders(): string[] {
  return journalStore.traders;
}

/** Import a curated setups file (e.g. aaron_overlay.json:
 * [{symbol,time,dir,entry,sl,tp,label}]) into the journal under `trader`. */
async function importTradesFromFile(file: File, trader: string): Promise<number> {
  const text = await file.text();
  // CSV (our own export, or a broker/platform export mapped by header) →
  // the tolerant column-mapped parser in the store.
  if (file.name.toLowerCase().endsWith(".csv") || (!text.trimStart().startsWith("[") && !text.trimStart().startsWith("{"))) {
    const entries = journalStore.parseCsv(text).map((e) => ({ ...e, trader }));
    if (entries.length) await journalStore.importTraderTrades(trader, entries);
    return entries.length;
  }
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return 0;
  }
  if (!Array.isArray(raw)) return 0;
  const entries: TradeEntry[] = [];
  for (const r of raw as Array<Record<string, unknown>>) {
    const sym = String(r.symbol ?? "").toUpperCase();
    const t = r.time ?? r.date;
    const ms = typeof t === "number" ? t : Date.parse(String(t));
    const entry = Number(r.entry ?? r.entryPrice);
    if (!sym || !Number.isFinite(ms) || !Number.isFinite(entry)) continue;
    const dir: TradeDirection = String(r.dir ?? r.direction ?? "").toLowerCase() === "short" ? "short" : "long";
    const num = (v: unknown) => (v != null && Number.isFinite(Number(v)) ? Number(v) : undefined);
    entries.push({
      id: crypto.randomUUID(),
      date: ms,
      symbol: sym,
      direction: dir,
      rMultiple: 0,
      setupTag: String(r.label ?? r.setupTag ?? "reference"),
      thesis: "",
      whatHappened: "",
      lesson: "",
      trader,
      entryPrice: entry,
      stopPrice: num(r.sl ?? r.stopPrice),
      targetPrice: num(r.tp ?? r.targetPrice),
    });
  }
  await journalStore.importTraderTrades(trader, entries);
  return entries.length;
}

function sectionHeader(text: string): HTMLElement {
  return el("div", { text, style: { font: "var(--font-header)", color: "var(--text-secondary)" } });
}

function textInput(placeholder: string, value: string, onInput: (v: string) => void): HTMLInputElement {
  const input = el("input", { attrs: { type: "text", placeholder } });
  input.value = value;
  input.addEventListener("input", () => onInput(input.value));
  return input;
}

function numberField(label: string, value: number, onChange: (v: number) => void): HTMLElement {
  const input = el("input", { attrs: { type: "text", inputmode: "decimal" }, style: { textAlign: "right", flex: "1", minWidth: "0" } });
  input.value = String(value);
  input.addEventListener("change", () => {
    const parsed = Number(input.value);
    if (Number.isFinite(parsed)) onChange(parsed);
    else input.value = String(value);
  });
  return el("div", { style: { display: "flex", alignItems: "center", gap: "6px" } }, [
    el("div", { text: label, style: { font: "var(--font-caption)", color: "var(--text-secondary)", width: "70px", flex: "none" } }),
    input,
  ]);
}

/** Cumulative-R equity sparkline over the full history (oldest → newest). */
function equityCurveCanvas(entries: TradeEntry[], width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  const dpr = window.devicePixelRatio || 1;
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  const ctx = canvas.getContext("2d")!;
  ctx.scale(dpr, dpr);

  const chronological = [...entries].sort((a, b) => a.date - b.date);
  const points: number[] = [0];
  for (const e of chronological) points.push(points[points.length - 1]! + e.rMultiple);
  const min = Math.min(...points, 0);
  const max = Math.max(...points, 0.1);
  const x = (i: number) => (i / (points.length - 1)) * (width - 4) + 2;
  const y = (v: number) => height - 4 - ((v - min) / (max - min)) * (height - 8);

  // Zero line.
  ctx.strokeStyle = "rgba(128,128,128,0.35)";
  ctx.lineWidth = 1;
  ctx.setLineDash([2, 3]);
  ctx.beginPath();
  ctx.moveTo(2, y(0));
  ctx.lineTo(width - 2, y(0));
  ctx.stroke();
  ctx.setLineDash([]);

  const final = points[points.length - 1]!;
  const stroke = final >= 0 ? "#26ad9c" : "#ef534f";
  ctx.strokeStyle = stroke;
  ctx.lineWidth = 1.6;
  ctx.lineJoin = "round";
  ctx.beginPath();
  points.forEach((v, i) => (i === 0 ? ctx.moveTo(x(i), y(v)) : ctx.lineTo(x(i), y(v))));
  ctx.stroke();
  // Emphasised endpoint.
  ctx.fillStyle = stroke;
  ctx.beginPath();
  ctx.arc(x(points.length - 1), y(final), 2.5, 0, Math.PI * 2);
  ctx.fill();
  return canvas;
}

/** Histogram of R outcomes in 0.5R buckets. */
function rHistogramCanvas(entries: TradeEntry[], width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  const dpr = window.devicePixelRatio || 1;
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  const ctx = canvas.getContext("2d")!;
  ctx.scale(dpr, dpr);

  const bucketOf = (r: number) => Math.max(-6, Math.min(6, Math.floor(r / 0.5)));
  const counts = new Map<number, number>();
  for (const e of entries) counts.set(bucketOf(e.rMultiple), (counts.get(bucketOf(e.rMultiple)) ?? 0) + 1);
  const buckets = [...counts.keys()].sort((a, b) => a - b);
  if (!buckets.length) return canvas;
  const maxCount = Math.max(...counts.values());
  const barW = Math.min(18, (width - 8) / buckets.length - 3);
  const totalW = buckets.length * (barW + 3) - 3;
  const startX = (width - totalW) / 2;
  buckets.forEach((bucket, i) => {
    const count = counts.get(bucket)!;
    const h = Math.max(2, (count / maxCount) * (height - 14));
    const bx = startX + i * (barW + 3);
    ctx.fillStyle = bucket >= 0 ? "#26ad9c" : "#ef534f";
    ctx.globalAlpha = 0.85;
    ctx.fillRect(bx, height - 12 - h, barW, h);
    ctx.globalAlpha = 1;
    ctx.fillStyle = "rgba(128,128,128,0.8)";
    ctx.font = "7px -apple-system, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(`${bucket * 0.5}`, bx + barW / 2, height - 3);
  });
  return canvas;
}

import { stockDirectory } from "../lib/stock-directory.js";
import { setupMemory } from "../lib/today.js";

export async function renderJournalPage(root: HTMLElement): Promise<() => void> {
  if (!storeInitialized) {
    storeInitialized = true;
    await journalStore.init();
  }

  // Persisted position-size settings (the native @AppStorage trio).
  let accountSize = await storageGet<number>("journalAccountSize", 50000);
  let riskPercent = await storageGet<number>("journalRiskPercent", 1.0);
  let pointValue = await storageGet<number>("journalPointValue", 100);

  const container = el("div", { style: { display: "flex", flexDirection: "column", gap: "10px", height: "100%", position: "relative" } });
  root.replaceChildren(container);

  // Fields flagged red after a declined "log incomplete" prompt. Lives
  // here (not in the module-level form state) so navigating away from the
  // page clears the highlights, as a fresh visit should look clean.
  let invalidFields = new Set<string>();

  async function logTrade() {
    const symbol = form.symbol.trim().toUpperCase();

    // Only a symbol is required — no nagging for a screenshot or reflection.
    // Automated-strategy loggers want R + a chart and out; manual traders can
    // add reflection if they choose. (Psychology is opt-in, not a gate.)
    if (!symbol) {
      invalidFields = new Set(["symbol"]);
      render();
      return;
    }
    invalidFields = new Set();

    const id = crypto.randomUUID();
    const entry: TradeEntry = {
      id,
      date: Date.now(),
      symbol,
      direction: form.direction,
      rMultiple: form.rMultiple,
      setupTag: form.selectedTag,
      thesis: form.thesis,
      whatHappened: form.whatHappened,
      lesson: form.lesson,
      timeframe: form.captureTimeframe ?? undefined,
      entryPrice: Number(form.entryPrice) > 0 ? Number(form.entryPrice) : undefined,
      exitPrice: Number(form.exitPrice) > 0 ? Number(form.exitPrice) : undefined,
      trader: form.activeTrader,
      session: form.session || undefined,
      dailyBias: form.dailyBias || undefined,
      entryType: form.entryType || undefined,
      targetPrice: numOrUndef(form.targetPrice),
      stopPrice: numOrUndef(form.stopPrice),
      size: numOrUndef(form.size),
      riskPct: numOrUndef(form.riskPct),
      ticks: numOrUndef(form.ticks),
      maxTicks: numOrUndef(form.maxTicks),
      timeHeldMinutes: numOrUndef(form.timeHeldMinutes),
      confluence1: form.confluence1 || undefined,
      confluence2: form.confluence2 || undefined,
      fees: numOrUndef(form.fees),
      netPnl: numOrUndef(form.netPnl),
      account: form.account || undefined,
      tvLink: form.tvLink || undefined,
      strategy: form.strategy || undefined,
      mode: form.mode,
    };
    await journalStore.addTrade(entry);

    form.captureTimeframe = null;
    form.symbol = "";
    form.rMultiple = 0;
    form.entryPrice = "";
    form.exitPrice = "";
    form.thesis = "";
    form.whatHappened = "";
    form.lesson = "";
    form.session = "";
    form.dailyBias = "";
    form.entryType = "";
    form.targetPrice = "";
    form.stopPrice = "";
    form.size = "";
    form.riskPct = "";
    form.ticks = "";
    form.maxTicks = "";
    form.timeHeldMinutes = "";
    form.confluence1 = "";
    form.confluence2 = "";
    form.fees = "";
    form.netPnl = "";
    form.account = "";
    form.tvLink = "";
    render();
  }

  /**
   * Renders the week as a clean shareable image (copied to the clipboard):
   * headline stats and the equity curve. Traders
   * love posting recaps — every share carries the product with it.
   */
  async function shareWeekReport() {
    const week = journalStore.thisWeekEntries;
    const W = 720;
    const H = 400;
    const canvas = document.createElement("canvas");
    canvas.width = W * 2;
    canvas.height = H * 2;
    const ctx = canvas.getContext("2d")!;
    ctx.scale(2, 2);

    ctx.fillStyle = "#1b1d21";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#26ad9c";
    ctx.fillRect(0, 0, W, 4);

    const weekLabel = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(new Date());
    ctx.fillStyle = "#eef0ee";
    ctx.font = "700 22px -apple-system, 'Segoe UI', sans-serif";
    ctx.fillText("My trading week", 32, 48);
    ctx.font = "500 12px 'SF Mono', ui-monospace, monospace";
    ctx.fillStyle = "#9a9a9e";
    ctx.fillText(`Week of ${weekLabel} · logged with TickAhead`, 32, 68);

    const wins = week.filter((e) => e.rMultiple > 0).length;
    const totalR = week.reduce((s, e) => s + e.rMultiple, 0);
    const stats: Array<[string, string, string]> = [
      ["TRADES", String(week.length), "#eef0ee"],
      ["WIN RATE", week.length ? `${Math.round((wins / week.length) * 100)}%` : "—", "#eef0ee"],
      ["NET R", `${totalR >= 0 ? "+" : ""}${totalR.toFixed(1)}R`, totalR >= 0 ? "#26ad9c" : "#ef534f"],
      ["AVG R", week.length ? `${(totalR / week.length).toFixed(2)}R` : "—", "#eef0ee"],
    ];
    stats.forEach(([label, value, color], i) => {
      const x = 32 + i * 168;
      ctx.fillStyle = "#6f7876";
      ctx.font = "600 10px 'SF Mono', ui-monospace, monospace";
      ctx.fillText(label, x, 108);
      ctx.fillStyle = color;
      ctx.font = "700 30px -apple-system, 'Segoe UI', sans-serif";
      ctx.fillText(value, x, 140);
    });

    // Equity curve of the whole journal, drawn large.
    const spark = equityCurveCanvas(viewEntries(), 656, 130);
    ctx.drawImage(spark, 32, 168, 656, 130);

    ctx.fillStyle = "#6f7876";
    ctx.font = "500 11px 'SF Mono', ui-monospace, monospace";
    ctx.fillText("tickahead.com", 32, H - 16);

    try {
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
      if (blob) {
        await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
        window.alert("Week report copied — paste it into any post.");
      }
    } catch {
      window.alert("Couldn't copy the report image — try again from the docked side panel.");
    }
  }

  function exportCSV() {
    const blob = new Blob([journalStore.csvString()], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = el("a");
    a.href = url;
    a.download = "TickAhead-Journal.csv";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }

  // (Snip-the-screen and file-attach removed for launch — trades are logged on
  // the chart and reconstructed from a candle snapshot, no images.)

  // --- New Entry tab -------------------------------------------------------

  function positionSizeCalculator(): HTMLElement {
    const dollarRisk = (accountSize * riskPercent) / 100;
    const suggestedSize = form.stopDistance > 0 && pointValue > 0 ? Math.floor(dollarRisk / (form.stopDistance * pointValue)) : 0;

    const box = el("div", { style: { background: "var(--bg-fill)", borderRadius: "8px", padding: "8px", display: "flex", flexDirection: "column", gap: "8px" } });
    const headerBtn = el(
      "button",
      {
        style: { display: "flex", alignItems: "center", gap: "6px", width: "100%" },
        onClick: () => {
          form.showSizeCalc = !form.showSizeCalc;
          render();
        },
      },
      [
        el("div", { text: "Position size", style: { font: "var(--font-header)", color: "var(--text-secondary)" } }),
        spacer(),
        ...(form.showSizeCalc
          ? []
          : [
              el("div", {
                text: `${suggestedSize} @ ${dollarRisk.toFixed(0)} risk`,
                style: { font: "var(--font-caption)", color: "var(--text-secondary)" },
              }),
            ]),
        svgIcon(form.showSizeCalc ? icons.chevronUp : icons.chevronDown, 10),
      ]
    );
    headerBtn.style.color = "var(--text-secondary)";
    box.appendChild(headerBtn);

    if (form.showSizeCalc) {
      box.appendChild(
        numberField("Account $", accountSize, (v) => {
          accountSize = v;
          void storageSet("journalAccountSize", v);
          render();
        })
      );
      box.appendChild(
        numberField("Risk %", riskPercent, (v) => {
          riskPercent = v;
          void storageSet("journalRiskPercent", v);
          render();
        })
      );
      box.appendChild(
        numberField("Stop (pts)", form.stopDistance, (v) => {
          form.stopDistance = v;
          render();
        })
      );
      box.appendChild(
        numberField("$ / point", pointValue, (v) => {
          pointValue = v;
          void storageSet("journalPointValue", v);
          render();
        })
      );
      box.appendChild(
        el("div", { style: { display: "flex", alignItems: "center" } }, [
          el("div", { text: `Risking ${dollarRisk.toFixed(0)}`, style: { font: "var(--font-caption)", color: "var(--text-secondary)" } }),
          spacer(),
          el("div", { text: `${suggestedSize} contracts`, style: { font: "var(--font-header)" } }),
        ])
      );
    }
    return box;
  }

  function newEntryTab(): HTMLElement {
    const tab = el("div", { className: "scroll no-scrollbar", style: { display: "flex", flexDirection: "column", gap: "10px", flex: "1" } });

    tab.appendChild(sectionHeader("Log trade"));

    // Symbol + direction — autocomplete from the same instrument directory
    // the watchlist uses (audit: "symbol is typed free-text despite the
    // app owning a full directory"). A native datalist keeps it light.
    const symbolInput = textInput("Symbol", form.symbol, (v) => {
      form.symbol = v;
      if (invalidFields.has("symbol") && v.trim()) {
        invalidFields.delete("symbol");
        symbolInput.style.borderColor = "var(--border)";
      }
      datalist.replaceChildren(
        ...stockDirectory.suggestions(v, null, 8).map((s) => {
          const option = el("option") as HTMLOptionElement;
          option.value = s.ticker;
          option.label = s.name;
          return option;
        })
      );
    });
    const datalist = el("datalist") as HTMLDataListElement;
    datalist.id = "journal-symbol-suggestions";
    symbolInput.setAttribute("list", datalist.id);
    tab.appendChild(datalist);
    void stockDirectory.ensureLoaded();
    symbolInput.style.flex = "1";
    symbolInput.style.minWidth = "0";
    if (invalidFields.has("symbol")) symbolInput.style.borderColor = "var(--accent-red)";
    const dirPicker = el("div", { style: { display: "flex", gap: "2px", flex: "none" } });
    for (const dir of ["long", "short"] as TradeDirection[]) {
      dirPicker.appendChild(
        el("button", {
          className: "pill" + (form.direction === dir ? " selected" : ""),
          text: dir === "long" ? "Long" : "Short",
          onClick: () => {
            form.direction = dir;
            render();
          },
        })
      );
    }
    tab.appendChild(el("div", { style: { display: "flex", gap: "6px", alignItems: "center" } }, [symbolInput, dirPicker]));

    // R stepper
    const rValue = el("div", {
      text: fmtR(form.rMultiple),
      style: {
        font: "var(--font-stat-value)",
        color: form.rMultiple >= 0 ? "var(--up)" : "var(--down)",
        flex: "1",
        textAlign: "center",
        fontVariantNumeric: "tabular-nums",
      },
    });
    tab.appendChild(
      el(
        "div",
        { style: { display: "flex", alignItems: "center", gap: "8px", background: "var(--bg-fill)", borderRadius: "6px", padding: "4px 6px" } },
        [
          el("div", { text: "Result", style: { font: "var(--font-body)", color: "var(--text-secondary)", width: "44px", flex: "none" } }),
          el("button", { text: "−", style: { fontSize: "14px", color: "var(--text-secondary)" }, onClick: () => {
            form.rMultiple = Math.round((form.rMultiple - 0.1) * 10) / 10;
            render();
          } }),
          rValue,
          el("button", { text: "+", style: { fontSize: "14px", color: "var(--text-secondary)" }, onClick: () => {
            form.rMultiple = Math.round((form.rMultiple + 0.1) * 10) / 10;
            render();
          } }),
        ]
      )
    );

    // Optional fill prices — one quiet row; skipping it costs nothing
    // today, filling it feeds the future exit-counterfactual analysis.
    const priceInput = (placeholder: string, value: string, onInput: (v: string) => void) => {
      const input = el("input", { attrs: { type: "text", inputmode: "decimal", placeholder }, style: { flex: "1", minWidth: "0" } });
      input.value = value;
      input.addEventListener("input", () => onInput(input.value));
      return input;
    };
    const fillsRow = el("div", { style: { display: "flex", gap: "6px", alignItems: "center" } }, [
      el("div", { text: "Fills", style: { font: "var(--font-body)", color: "var(--text-secondary)", width: "44px", flex: "none" }, attrs: { title: "Optional — entry and exit price. Powers future exit analysis." } }),
      priceInput("Entry (optional)", form.entryPrice, (v) => (form.entryPrice = v)),
      priceInput("Exit (optional)", form.exitPrice, (v) => (form.exitPrice = v)),
    ]);

    // Details (Sean-parity fields) — collapsed by default so the R-first core
    // stays lean; expand to log session, bias, levels, size, ticks, ledger.
    const detailsToggle = el("button", {
      className: "pill",
      text: form.showDetails ? "Hide details" : "Add details (session, levels, size, ledger)…",
      style: { flex: "none", alignSelf: "flex-start" },
      onClick: () => {
        form.showDetails = !form.showDetails;
        render();
      },
    });
    tab.appendChild(detailsToggle);
    if (form.showDetails) {
      const labeledRow = (label: string, control: HTMLElement) =>
        el("div", { style: { display: "flex", gap: "6px", alignItems: "center", flex: "1" } }, [
          el("div", { text: label, style: { font: "var(--font-caption)", color: "var(--text-secondary)", width: "84px", flex: "none" } }),
          control,
        ]);
      const selectRow = (label: string, value: string, options: string[], onChange: (v: string) => void) => {
        const sel = el("select", { style: { flex: "1", minWidth: "0" } }) as HTMLSelectElement;
        for (const o of ["", ...options]) {
          const opt = el("option") as HTMLOptionElement;
          opt.value = o;
          opt.text = o || "—";
          if (o === value) opt.selected = true;
          sel.appendChild(opt);
        }
        sel.addEventListener("change", () => onChange(sel.value));
        return labeledRow(label, sel);
      };
      const textRow = (label: string, value: string, placeholder: string, onInput: (v: string) => void, decimal = false) => {
        const input = el("input", { attrs: { type: "text", placeholder, ...(decimal ? { inputmode: "decimal" } : {}) }, style: { flex: "1", minWidth: "0" } }) as HTMLInputElement;
        input.value = value;
        input.addEventListener("input", () => onInput(input.value));
        return labeledRow(label, input);
      };
      const details = el("div", { style: { display: "flex", flexDirection: "column", gap: "6px", padding: "6px 0" } }, [
        selectRow("Session", form.session, ["Asia", "Pre-London", "London", "Pre NY", "New York"], (v) => (form.session = v)),
        selectRow("Daily bias", form.dailyBias, ["Bullish", "Bearish", "Neutral"], (v) => (form.dailyBias = v)),
        selectRow("Entry type", form.entryType, ["Market", "Limit", "Stop Market"], (v) => (form.entryType = v)),
        el("div", { style: { display: "flex", gap: "6px" } }, [
          textRow("Target", form.targetPrice, "price", (v) => (form.targetPrice = v), true),
          textRow("Stop", form.stopPrice, "price", (v) => (form.stopPrice = v), true),
        ]),
        el("div", { style: { display: "flex", gap: "6px" } }, [
          textRow("Size", form.size, "contracts", (v) => (form.size = v), true),
          textRow("Risk %", form.riskPct, "%", (v) => (form.riskPct = v), true),
        ]),
        el("div", { style: { display: "flex", gap: "6px" } }, [
          textRow("Ticks", form.ticks, "result", (v) => (form.ticks = v), true),
          textRow("Max ticks", form.maxTicks, "MFE", (v) => (form.maxTicks = v), true),
        ]),
        el("div", { style: { display: "flex", gap: "6px" } }, [
          textRow("Confluence 1", form.confluence1, "e.g. VWAP", (v) => (form.confluence1 = v)),
          textRow("Confluence 2", form.confluence2, "e.g. level", (v) => (form.confluence2 = v)),
        ]),
        textRow("Time held (mins)", form.timeHeldMinutes, "minutes", (v) => (form.timeHeldMinutes = v), true),
        el("div", { text: "Ledger (optional)", style: { font: "var(--font-caption)", color: "var(--text-secondary)", paddingTop: "2px" } }),
        el("div", { style: { display: "flex", gap: "6px" } }, [
          textRow("Net P/L", form.netPnl, "amount", (v) => (form.netPnl = v), true),
          textRow("Fees", form.fees, "amount", (v) => (form.fees = v), true),
        ]),
        el("div", { style: { display: "flex", gap: "6px" } }, [
          textRow("Account", form.account, "account", (v) => (form.account = v)),
          textRow("TradingView link", form.tvLink, "url", (v) => (form.tvLink = v)),
        ]),
      ]);
      // Fills live in here (secondary to the R-first essentials above). The
      // position-size calculator is removed for launch.
      details.insertBefore(fillsRow, details.firstChild);
      tab.appendChild(details);
    }

    // Tags
    const tagRow = el("div", { style: { display: "flex", gap: "6px", overflowX: "auto", alignItems: "center" } });
    for (const tag of journalStore.allTags) {
      const chip = el("button", {
        className: "pill" + (form.selectedTag === tag ? " selected" : ""),
        text: tag,
        onClick: () => {
          form.selectedTag = tag;
          render();
        },
      });
      if (form.selectedTag === tag) {
        chip.style.background = "var(--accent-blue)";
        chip.style.color = "#fff";
      }
      tagRow.appendChild(chip);
    }
    const addTagBtn = el("button", { attrs: { "aria-label": "Add tag" }, style: { color: "var(--text-secondary)", flex: "none", display: "flex" } });
    addTagBtn.appendChild(svgIcon(icons.plus, 12));
    addTagBtn.addEventListener("click", () => {
      form.showAddTag = !form.showAddTag;
      render();
    });
    tagRow.appendChild(addTagBtn);
    tab.appendChild(tagRow);

    // Strategy link + mode — ties the trade to strategy development and
    // separates practice (paper) from real money (live). Mode is always
    // shown (useful for the practice persona); Strategy appears once you have
    // saved strategies to link to.
    const strategyNames = (() => {
      const names = new Set<string>(listSavedStrategies());
      try { const cur = loadStrategy(); if (cur?.name) names.add(cur.name); } catch { /* ignore */ }
      return [...names];
    })();
    const modePicker = el("div", { style: { display: "flex", gap: "2px", flex: "none" } });
    for (const m of ["manual", "paper", "live"] as const) {
      modePicker.appendChild(el("button", {
        className: "pill" + (form.mode === m ? " selected" : ""),
        text: m.charAt(0).toUpperCase() + m.slice(1),
        onClick: () => { form.mode = m; render(); },
      }));
    }
    const labelCol = (t: string) => el("div", { text: t, style: { font: "var(--font-caption)", color: "var(--text-secondary)", width: "56px", flex: "none" } });
    if (strategyNames.length) {
      const stratSel = el("select", { style: { flex: "1", minWidth: "0" } }) as HTMLSelectElement;
      for (const o of ["", ...strategyNames]) {
        const opt = el("option") as HTMLOptionElement;
        opt.value = o; opt.text = o || "No strategy";
        if (o === form.strategy) opt.selected = true;
        stratSel.appendChild(opt);
      }
      stratSel.addEventListener("change", () => (form.strategy = stratSel.value));
      tab.appendChild(el("div", { style: { display: "flex", gap: "6px", alignItems: "center" } }, [labelCol("Strategy"), stratSel]));
    }
    tab.appendChild(el("div", { style: { display: "flex", gap: "6px", alignItems: "center" } }, [labelCol("Mode"), modePicker]));

    // Memory Engine: picking a setup replays your history with it — the
    // trader's own words at the moment of maximum leverage.
    const tagMemory = form.selectedTag ? setupMemory(viewEntries(), form.selectedTag) : null;
    if (tagMemory) {
      const r = tagMemory.totalR;
      let text = `${form.selectedTag}: ${tagMemory.trades}× · ${r >= 0 ? "+" : ""}${r.toFixed(1)}R · ${Math.round(tagMemory.winRate)}% wins`;
      if (tagMemory.lastLesson) text += ` — last time you wrote: “${tagMemory.lastLesson}”`;
      tab.appendChild(
        el("div", {
          text,
          style: { font: "var(--font-caption)", color: r >= 0 ? "var(--text-secondary)" : "var(--accent-orange)" },
        })
      );
    }

    if (form.showAddTag) {
      const newTagInput = el("input", { attrs: { type: "text", placeholder: "New tag" } });
      newTagInput.addEventListener("keydown", (e) => {
        if (e.key !== "Enter") return;
        const tag = newTagInput.value.trim();
        if (!tag) return;
        void journalStore.addCustomTag(tag).then(() => {
          form.selectedTag = tag;
          form.showAddTag = false;
          render();
        });
      });
      tab.appendChild(newTagInput);
      setTimeout(() => newTagInput.focus(), 0);
    }

    // Chart image + reflection removed for launch: trades are placed and logged
    // on the chart (in-app only), which reconstructs them from a candle snapshot
    // — no screenshot, no note-gate.

    // Log button
    tab.appendChild(
      el("button", {
        text: "Log trade",
        style: {
          font: "var(--font-button)",
          padding: "6px",
          borderRadius: "6px",
          background: "var(--bg-fill-strong)",
          width: "100%",
        },
        onClick: () => void logTrade(),
      })
    );

    // Trader picker + trades-import removed for launch: the journal is your own
    // in-app trades only (single trader, "Me").

    return tab;
  }

  // --- Review tab ------------------------------------------------------

  function metricCard(label: string, value: string, color: string): HTMLElement {
    return el(
      "div",
      {
        style: {
          flex: "1",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "2px",
          padding: "8px 0",
          background: "var(--bg-fill)",
          borderRadius: "8px",
        },
      },
      [
        el("div", { text: value, style: { font: "var(--font-stat-value)", color, fontVariantNumeric: "tabular-nums" } }),
        el("div", { text: label, style: { font: "var(--font-caption)", color: "var(--text-secondary)" } }),
      ]
    );
  }

  function reviewTab(): HTMLElement {
    const tab = el("div", { className: "scroll no-scrollbar", style: { display: "flex", flexDirection: "column", gap: "10px", flex: "1" } });

    // First-run: dash-filled metrics over an empty list teach nothing.
    // Say what belongs here and hand over the button that starts it.
    if (viewEntries().length === 0) {
      tab.appendChild(
        el(
          "div",
          {
            style: {
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              flex: "1",
              minHeight: "180px",
              textAlign: "center",
              padding: "0 20px",
            },
          },
          [
            el("div", { text: "No trades yet", style: { font: "var(--font-body)", color: "var(--text-secondary)" } }),
            el("div", {
              text: "Deploy a strategy (to your PC, a server, or your Pi) and its trades land here automatically — win rate, equity curve, and setup stats build as it runs.",
              style: { font: "var(--font-caption)", color: "var(--text-secondary-faint)" },
            }),
          ]
        )
      );
      return tab;
    }

    // Just the trade list (with filters) — the stats blocks were removed; slice
    // by symbol / setup / result / strategy right here instead.
    const recentHeader = el("div", { style: { display: "flex", alignItems: "center", gap: "8px" } }, [
      sectionHeader("Recent"),
      spacer(),
    ]);
    // Open the journal as a full browser tab for more room.
    if (location.protocol === "chrome-extension:" && !new URLSearchParams(location.search).has("tab")) {
      const expandBtn = el("button", {
        attrs: { title: "Open full view in a tab", "aria-label": "Open full view in a tab" },
        style: { color: "var(--text-secondary)", display: "flex", flex: "none" },
      });
      expandBtn.appendChild(svgIcon(icons.expand, 13));
      expandBtn.addEventListener("click", () => { if (typeof chrome !== "undefined" && chrome.tabs?.create) void chrome.tabs.create({ url: chrome.runtime.getURL("popup.html?tab=1&page=2") }); });
      recentHeader.appendChild(expandBtn);
    }
    recentHeader.appendChild(
      el("button", { text: "Export CSV", style: { font: "var(--font-caption)", color: "var(--text-secondary)" }, onClick: exportCSV })
    );
    tab.appendChild(recentHeader);

    // Filters appear once history is big enough to need them (audit).
    const allEntries = viewEntries();
    if (allEntries.length >= 6 || form.reviewFilterSymbol || form.reviewFilterTag || form.reviewFilterStrategy || form.reviewFilterOutcome) {
      const filterRow = el("div", { style: { display: "flex", gap: "4px", flexWrap: "wrap" } });
      const select = (value: string, options: Array<[string, string]>, onChange: (v: string) => void) => {
        const node = el("select") as HTMLSelectElement;
        Object.assign(node.style, {
          font: "var(--font-caption)",
          background: "var(--bg-fill)",
          color: "var(--text-primary)",
          border: "1px solid var(--border)",
          borderRadius: "6px",
          padding: "2px 4px",
          maxWidth: "96px",
        });
        for (const [v, label] of options) {
          const option = el("option") as HTMLOptionElement;
          option.value = v;
          option.textContent = label;
          if (v === value) option.selected = true;
          node.appendChild(option);
        }
        node.addEventListener("change", () => onChange(node.value));
        return node;
      };
      const symbols = [...new Set(allEntries.map((e) => e.symbol).filter(Boolean))].sort();
      const tags = [...new Set(allEntries.map((e) => e.setupTag).filter(Boolean))].sort();
      const strategies = [...new Set(allEntries.map((e) => e.strategy).filter((s): s is string => !!s))].sort();
      filterRow.appendChild(
        select(form.reviewFilterSymbol, [["", "All symbols"], ...symbols.map((s): [string, string] => [s, s])], (v) => {
          form.reviewFilterSymbol = v;
          render();
        })
      );
      if (strategies.length) {
        filterRow.appendChild(
          select(form.reviewFilterStrategy, [["", "All strategies"], ...strategies.map((s): [string, string] => [s, s])], (v) => {
            form.reviewFilterStrategy = v;
            render();
          })
        );
      }
      filterRow.appendChild(
        select(form.reviewFilterTag, [["", "All setups"], ...tags.map((t): [string, string] => [t, t])], (v) => {
          form.reviewFilterTag = v;
          render();
        })
      );
      filterRow.appendChild(
        select(form.reviewFilterOutcome, [["", "All results"], ["win", "Wins"], ["loss", "Losses"]], (v) => {
          form.reviewFilterOutcome = v as typeof form.reviewFilterOutcome;
          render();
        })
      );
      tab.appendChild(filterRow);
    }
    const filteredEntries = allEntries.filter(
      (e) =>
        (!form.reviewFilterSymbol || e.symbol === form.reviewFilterSymbol) &&
        (!form.reviewFilterTag || e.setupTag === form.reviewFilterTag) &&
        (!form.reviewFilterStrategy || e.strategy === form.reviewFilterStrategy) &&
        (!form.reviewFilterOutcome || (form.reviewFilterOutcome === "win" ? e.rMultiple > 0 : e.rMultiple <= 0))
    );

    const list = el("div", { style: { display: "flex", flexDirection: "column" } });
    for (const entry of filteredEntries) {
      const row = el(
        "div",
        {
          style: { display: "flex", alignItems: "center", gap: "8px", padding: "5px 0", cursor: "pointer" },
          onClick: () => {
            form.reviewingEntryID = entry.id;
            render();
          },
        },
        [
          el("div", { text: entry.symbol, style: { font: "var(--font-header)", width: "46px", flex: "none" } }),
          el("div", {
            text: entry.setupTag,
            style: { font: "var(--font-caption)", color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
          }),
          spacer(),
          el("div", {
            text: fmtR(entry.rMultiple),
            style: { font: "var(--font-mono-value)", color: entry.rMultiple >= 0 ? "var(--up)" : "var(--down)" },
          }),
        ]
      );
      const delBtn = closeButton(() => {}, { label: `Delete ${entry.symbol} trade`, size: 12 });
      Object.assign(delBtn.style, { width: "20px", height: "20px" });
      delBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        void journalStore.deleteTrade(entry.id);
      });
      row.appendChild(delBtn);
      list.appendChild(row);
      list.appendChild(divider());
    }
    tab.appendChild(list);
    return tab;
  }

  // --- Overlays ------------------------------------------------------

  function overlayCard(): HTMLElement {
    return el("div", {
      style: {
        position: "absolute",
        inset: "0",
        background: "var(--bg)",
        borderRadius: "12px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        zIndex: "10",
      },
    });
  }

  function notesOverlay(): HTMLElement {
    const card = overlayCard();
    const closeBtn = closeButton(() => {}, { label: "Close notes", size: 14 });
    closeBtn.addEventListener("click", () => {
      form.showNotes = false;
      if (form.thesis.trim() || form.whatHappened.trim() || form.lesson.trim()) invalidFields.delete("notes");
      render();
    });
    card.appendChild(
      el("div", { style: { display: "flex", alignItems: "center" } }, [
        el("div", {
          text: `Notes — ${form.symbol.trim() || "New trade"} ${fmtR(form.rMultiple)}`,
          style: { font: "var(--font-title)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
        }),
        spacer(),
        closeBtn,
      ])
    );

    const fields = el("div", { className: "scroll", style: { display: "flex", flexDirection: "column", gap: "10px", flex: "1" } });
    const noteField = (title: string, hint: string, get: () => string, set: (v: string) => void) => {
      const textarea = el("textarea", { attrs: { placeholder: hint } }) as unknown as HTMLTextAreaElement;
      Object.assign(textarea.style, {
        font: "var(--font-body)",
        color: "var(--text-primary)",
        background: "var(--bg-fill)",
        border: "1px solid var(--border)",
        borderRadius: "6px",
        padding: "6px",
        minHeight: "56px",
        resize: "vertical",
      });
      textarea.value = get();
      textarea.addEventListener("input", () => set(textarea.value));
      return el("div", { style: { display: "flex", flexDirection: "column", gap: "3px" } }, [
        el("div", { text: title, style: { font: "var(--font-label)", color: "var(--accent-blue)" } }),
        textarea,
      ]);
    };
    fields.appendChild(noteField("Thesis", "Why did you take this trade?", () => form.thesis, (v) => (form.thesis = v)));
    fields.appendChild(noteField("What happened", "Did you follow the plan?", () => form.whatHappened, (v) => (form.whatHappened = v)));
    fields.appendChild(noteField("Lesson", "What would you do differently?", () => form.lesson, (v) => (form.lesson = v)));
    card.appendChild(fields);
    return card;
  }

  function reviewOverlay(entry: TradeEntry): HTMLElement {
    const card = overlayCard();
    const closeBtn = closeButton(() => {}, { label: "Close review", size: 14 });
    closeBtn.addEventListener("click", () => {
      form.reviewingEntryID = null;
      render();
    });
    card.appendChild(
      el("div", { style: { display: "flex", alignItems: "center" } }, [
        el("div", { text: `${entry.symbol || "Trade"} ${fmtR(entry.rMultiple)}`, style: { font: "var(--font-title)" } }),
        spacer(),
        closeBtn,
      ])
    );

    const body = el("div", { className: "scroll", style: { display: "flex", flexDirection: "column", gap: "10px", flex: "1" } });
    body.appendChild(
      el("div", { style: { display: "flex", gap: "12px", font: "var(--font-caption)", color: "var(--text-secondary)", alignItems: "center" } }, [
        el("div", { text: entry.direction === "long" ? "↑ Long" : "↓ Short" }),
        el("div", { text: `⌁ ${entry.setupTag}` }),
        ...(entry.strategy ? [el("div", { text: `▤ ${entry.strategy}`, style: { color: "var(--accent-blue)" } })] : []),
        ...(entry.mode && entry.mode !== "manual" ? [el("div", { text: entry.mode.toUpperCase(), style: { color: entry.mode === "live" ? "var(--accent-red)" : "var(--text-secondary)" } })] : []),
        el("div", { text: new Date(entry.date).toLocaleDateString() }),
        spacer(),
      ])
    );


    // Trade facts — the structured detail a deployed trade carries (prices,
    // size, R, P/L, time held). Only rows with a value are shown.
    const fmtPx = (n: number) => (Math.abs(n) < 10 ? n.toFixed(4) : n.toFixed(2));
    const fmtHeld = (m: number) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);
    const factRows: Array<[string, string | null]> = [
      ["Entry", entry.entryPrice != null ? fmtPx(entry.entryPrice) : null],
      ["Exit", entry.exitPrice != null ? fmtPx(entry.exitPrice) : null],
      ["Stop", entry.stopPrice != null ? fmtPx(entry.stopPrice) : null],
      ["Target", entry.targetPrice != null ? fmtPx(entry.targetPrice) : null],
      ["Size", entry.size != null ? String(entry.size) : null],
      ["Result", `${fmtR(entry.rMultiple)}`],
      ["Net P/L", entry.netPnl != null ? (entry.netPnl >= 0 ? "+" : "") + entry.netPnl.toFixed(2) : null],
      ["Held", entry.timeHeldMinutes != null ? fmtHeld(entry.timeHeldMinutes) : null],
    ];
    const factsGrid = el("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px" } });
    for (const [label, value] of factRows) {
      if (value == null) continue;
      factsGrid.appendChild(el("div", { style: { display: "flex", justifyContent: "space-between", gap: "8px" } }, [
        el("span", { text: label, style: { font: "var(--font-caption)", color: "var(--text-secondary)" } }),
        el("span", { text: value, style: { font: "var(--font-mono-value)", color: label === "Result" ? (entry.rMultiple >= 0 ? "var(--up)" : "var(--down)") : "var(--text-primary)" } }),
      ]));
    }
    if (factsGrid.childElementCount) body.appendChild(factsGrid);

    const reviewField = (title: string, text: string) => {
      if (!text) return null;
      return el("div", { style: { display: "flex", flexDirection: "column", gap: "3px" } }, [
        el("div", { text: title, style: { font: "var(--font-label)", color: "var(--accent-blue)" } }),
        el("div", { text, style: { font: "var(--font-body)", whiteSpace: "pre-wrap" } }),
      ]);
    };
    for (const field of [
      reviewField("Thesis", entry.thesis),
      reviewField("What happened", entry.whatHappened),
      reviewField("Lesson", entry.lesson),
    ]) {
      if (field) body.appendChild(field);
    }
    card.appendChild(body);
    return card;
  }

  // --- Render ------------------------------------------------------

  function render() {
    container.replaceChildren();

    // Review-only: trades come from deployed strategies (paper/live), not manual
    // entry, so there's no tab bar — the Journal is the list of what your
    // deployments did.
    const tabContent = reviewTab();
    container.appendChild(tabContent);

    const reviewing = form.reviewingEntryID ? viewEntries().find((e) => e.id === form.reviewingEntryID) : null;
    if (reviewing) {
      // Fully hide the underlying list while the review overlay is up.
      tabContent.style.visibility = "hidden";
      container.appendChild(reviewOverlay(reviewing));
    }
  }

  // Opened here from a "View on chart" click on another surface (the popup that
  // then docked this side panel): land on the trade's detail overlay.
  const openReq = await storageGet<{ id?: string; at?: number } | null>("journalOpenTrade", null);
  if (openReq?.id && (openReq.at == null || Date.now() - openReq.at < 8000)) {
    form.reviewingEntryID = openReq.id;
    void storageSet("journalOpenTrade", null);
  }

  render();

  const unsub = journalStore.changed.subscribe(() => {
    render();
  });
  // A later "View on chart" (panel already open) opens that trade's detail live.
  const unsubOpen = onStorageChange<{ id?: string } | null>("journalOpenTrade", (v) => {
    if (v?.id) { form.reviewingEntryID = v.id; void storageSet("journalOpenTrade", null); render(); }
  });
  return () => { unsub(); unsubOpen(); };
}
